import { useTranslation } from "react-i18next";
import type { Project } from "senf-shared/types";
import * as yup from "yup";

export function useSchemaValidation() {
  const { t } = useTranslation();
  const schema = {
    email: yup
      .string()
      .trim()
      .required(t("auth:warning.enterEmail"))
      .matches(/^\S*$/, t("auth:warning.spacesNotAllowed"))
      .email(t("auth:warning.enterValidEmail")),

    password: yup
      .string()
      .trim()
      .required(t("auth:warning.enterValidPassword"))
      .matches(/^\S*$/, t("auth:warning.spacesNotAllowed"))
      .min(8, t("auth:warning.passwordEightCharacters"))
      .matches(/\d+/, t("auth:warning.passwordWithNumber")),

    confirmPassword: yup
      .string()
      .trim()
      .required(t("auth:warning.confirmPassword"))
      .oneOf([yup.ref("password"), null], t("auth:warning.passwordsMustMatch")),
    handle: yup
      .string()
      .trim()
      .required(t("auth:warning.enterUsername"))
      .nullable()
      .min(3, t("util:tooShort", { val: t("global:username") }))
      .max(20, t("util:tooLong", { val: t("global:username") }))
      // .matches(/^\S*$/, t("auth:warning.spacesNotAllowed"))
      .matches(/^[a-zA-Z0-9äöüÄÖÜ ._-]+$/, t("auth:warning.usernameLatinOnly")),
    title: (warning?: string, min?: number, max?: number) =>
      yup
        .string()
        .required(t("util:input", { val: `${warning || t("global:title")}` }))
        .min(
          min || 3,
          t("util:tooShort", { val: `${warning || t("global:title")}` }),
        )
        .max(
          max || 30,
          t("util:tooLong", { val: `${warning || t("global:title")}` }),
        ),

    description: (warning?: string, min?: number, max?: number) =>
      yup
        .string()
        .required(
          t("util:input", { val: `${warning || t("global:description")}` }),
        )
        .min(
          min || 10,
          t("util:tooShort", { val: `${warning || t("global:description")}` }),
        )
        .max(
          max || 100,
          t("util:tooLong", { val: `${warning || t("global:description")}` }),
        ),
    postcode: yup
      .string()
      // .matches(/^\d{4,5}$/, t("auth:warning.enterValidPostCode"))
      .required(t("auth:warning.enterPostCode")),
    gender: yup.string().required(t("auth:warning.enterGender")),
    age: yup.string().required(t("auth:warning.enterBirthYear")),
  };

  const email = yup.object({
    email: schema.email,
  });
  const login = yup.object({
    email: schema.email,
    password: yup
      .string()
      .trim()
      .required(t("auth:warning.enterValidPassword")),
  });
  const register = yup.object({
    email: schema.email,
    password: schema.password,
    confirmPassword: schema.confirmPassword,
    handle: schema.handle,
  });
  const invite = yup.object({
    email: schema.email,
    handle: schema.handle.optional(),
  });

  const addUserDetails = (
    project?: Project,
    translation?: Record<string, string>,
  ) => {
    // Get the dynamic demographic fields from project settings
    const dynamicFields = project?.settings?.demographicData?.fields || [];

    // Start with base schema
    const baseSchema: Record<string, yup.AnySchema> = {
      age: yup
        .string()
        .nullable()
        .required(
          t("util:isRequired", { val: t("auth:authOptions.birthYear") }),
        ),
      gender: yup
        .string()
        .nullable()
        .required(t("util:isRequired", { val: t("auth:authOptions.gender") })),
      // gender: yup.string().nullable().required(t("auth:warning.enterGender")),
      // age: yup.string().nullable().required(t("auth:warning.enterBirthYear")),
    };

    // Add postcode if it's required
    if (!project?.settings?.demographicData?.withoutPostcode) {
      baseSchema.postcode = yup
        .string()
        .nullable()
        // .matches(/^\d{5}$/, t("auth:warning.enterValidPostCode"))
        // .required(t("auth:warning.enterPostCode"));
        .required(
          t("util:isRequired", { val: t("auth:authOptions.postcode") }),
        );
    }

    // Add dynamic required fields to schema
    for (const field of dynamicFields) {
      // if (field.required) {}
      baseSchema[field.name] = yup.string().required(
        t("util:isRequired", {
          val: translation?.[field.label] || field.label,
        }),
      );
    }

    return yup.object().shape(baseSchema);
  };

  const surveySignIn = yup.object().shape({
    providerId: yup.string().optional(),
    email: yup.string().when("providerId", {
      is: (providerId: string) => providerId === "anonymous",
      then: yup.string().optional(),
      otherwise: schema.email.optional(),
    }),
    handle: yup.string().when("providerId", {
      is: (providerId: string) => !providerId,
      then: schema.handle,
      otherwise: yup.string().optional(),
    }),
    password: yup.string().when("providerId", {
      is: (providerId: string) => providerId,
      then: yup.string().when("providerId", {
        is: (providerId: string) => providerId === "password",
        then: yup
          .string()
          .trim()
          .required(t("auth:warning.enterValidPassword")),
        otherwise: yup.string().optional(),
      }),
      otherwise: schema.password,
    }),
    confirmPassword: yup.string().when("providerId", {
      is: (providerId: string) => !providerId,
      then: schema.confirmPassword,
      otherwise: yup.string().optional(),
    }),
  });
  const support = yup.object({
    email: schema.email,
    reason: schema.description("description", 5, 500),
  });

  /**
   *  This function creates a yup schema for each object in the array
   * @param arrayOfObjects array of objects with one key and one value
   * @returns yup schema
   * @example
   * validation([
      {
        title: "ideaTitle",
        min: 10,
        max: 70,
      },
      {
        body: "ideaDescription",
        min: 100,
        max: 800,
      },
    ])
   */
  const validation = (arrayOfObjects) => {
    const validators = arrayOfObjects.reduce((acc, key) => {
      // take the first key of the each object
      const yupKey = Object.keys(key)[0];
      // take the value of the first key of the object
      const yupValue = key[yupKey];

      // create a yup schema for that key
      acc[yupKey] = yup
        .string()
        .required(t("util:input", { val: `${yupValue}` }))
        .min(key.min || 5, t("util:tooShort", { val: `${yupValue}` }))
        .max(key.max || 50, t("util:tooLong", { val: `${yupValue}` }));

      return acc;
    }, {});

    return yup.object(validators);
  };

  const validate = {
    email,
    login,
    register,
    addUserDetails,
    surveySignIn,
    support,
    invite,
    validation,
  };
  return validate;
}
